/** SUNTRONIX ONE — Contract Control Centre.
 *
 *  Single authoritative surface for contracts, entitlements, dashboard
 *  counts and eligibility resolution.  Consumed by Preflight, Orders,
 *  Deployments etc via the /api/contract-centre/eligibility resolver.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/app/PageHeader";
import Section from "@/components/app/Section";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  FileText, RefreshCcw, Search, Plus, ShieldCheck,
  AlertTriangle, Clock, Ban,
} from "lucide-react";

const STATUS_TONE = {
  DRAFT:             "bg-slate-100 text-slate-700",
  UNDER_REVIEW:      "bg-sky-100 text-sky-800",
  APPROVAL_REQUIRED: "bg-amber-100 text-amber-800",
  ACTIVE:            "bg-emerald-100 text-emerald-800",
  EXPIRING:          "bg-amber-100 text-amber-900",
  EXPIRED:           "bg-red-100 text-red-800",
  SUSPENDED:         "bg-orange-100 text-orange-800",
  TERMINATED:        "bg-slate-200 text-slate-500 line-through",
  SUPERSEDED:        "bg-slate-200 text-slate-500",
  CANCELLED:         "bg-slate-200 text-slate-500 line-through",
};

export default function ContractCentre() {
  const { user } = useAuth();
  const isSuntronix = !user?.master_company_id;
  const [dashboard, setDashboard] = useState(null);
  const [contracts, setContracts] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showEligibility, setShowEligibility] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const [d, list] = await Promise.all([
        api.get("/contract-centre/dashboard"),
        api.get("/contract-centre/contracts", {
          params: { status: statusFilter || undefined, q: q || undefined },
        }),
      ]);
      setDashboard(d.data);
      setContracts(list.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Load failed");
    } finally { setBusy(false); }
  }, [statusFilter, q]);

  useEffect(() => { load(); }, [load]);

  const tiles = useMemo(() => {
    if (!dashboard) return [];
    return [
      { key: "active",  label: "Active",           n: dashboard.active,                 tone: "text-emerald-700" },
      { key: "exp30",   label: "Expiring 30 days", n: dashboard.expiring_30d,           tone: "text-amber-700"   },
      { key: "exp60",   label: "Expiring 60 days", n: dashboard.expiring_60d,           tone: "text-amber-700"   },
      { key: "expired", label: "Expired",          n: dashboard.expired,                tone: "text-red-700"     },
      { key: "renew",   label: "Renewal action",   n: dashboard.renewal_action_required, tone: "text-slate-900"  },
      { key: "no_ent",  label: "No entitlements",  n: dashboard.services_without_entitlement, tone: "text-slate-700" },
      { key: "no_rate", label: "Rate missing",     n: dashboard.rate_missing,           tone: "text-amber-800"   },
      { key: "no_sla",  label: "SLA missing",      n: dashboard.sla_missing,            tone: "text-amber-800"   },
    ];
  }, [dashboard]);

  return (
    <div className="space-y-6 max-w-[1400px]" data-testid="contract-centre">
      <PageHeader
        eyebrow="Commercial · Contracts"
        title="Contract Control Centre"
        context="The commercial source of truth — contracts, entitlements, SLAs, rates.  Every operational transaction is authorised here before it begins."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setShowEligibility(true)}
                    data-testid="cc-eligibility-btn">
              <ShieldCheck className="h-3.5 w-3.5 mr-1.5" /> Test eligibility
            </Button>
            {isSuntronix && (
              <Button onClick={() => setShowNew(true)} data-testid="cc-new-btn">
                <Plus className="h-3.5 w-3.5 mr-1.5" /> New contract
              </Button>
            )}
            <Button variant="outline" onClick={load} disabled={busy}>
              <RefreshCcw className={`h-3.5 w-3.5 mr-1.5 ${busy ? "animate-spin" : ""}`} /> Refresh
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
        {tiles.map((t) => (
          <button key={t.key}
            onClick={() => {
              if (t.key === "active") setStatusFilter("ACTIVE");
              else if (t.key === "expired") setStatusFilter("EXPIRED");
              else if (t.key === "exp30" || t.key === "exp60") setStatusFilter("EXPIRING");
              else setStatusFilter("");
            }}
            className="st-panel p-3 text-left hover:bg-slate-50 transition-colors"
            data-testid={`cc-tile-${t.key}`}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{t.label}</div>
            <div className={`text-2xl font-heading font-semibold tabular-nums ${t.tone}`}>{t.n ?? 0}</div>
          </button>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2 top-2.5" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                     placeholder="Contract # or name…"
                     className="pl-7 h-9 text-[13px] max-w-sm"
                     data-testid="cc-search" />
        </div>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                    className="border rounded h-9 px-2 text-[12px]"
                    data-testid="cc-status-filter">
          <option value="">All statuses</option>
          {Object.keys(STATUS_TONE).map((s) => <option key={s}>{s}</option>)}
        </select>
        <div className="text-[11px] text-slate-500 ml-auto">{contracts.length} contracts</div>
      </div>

      <Section title="Contracts" testId="cc-list">
        <div className="st-panel overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Number</th>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Countries</th>
                <th className="text-left px-3 py-2">Term</th>
                <th className="text-left px-3 py-2">Currency</th>
                <th className="text-left px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {contracts.map((c) => (
                <tr key={c.id}
                    onClick={() => setSelectedId(c.id)}
                    className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                    data-testid={`cc-row-${c.id}`}>
                  <td className="px-3 py-2 font-mono text-[12px]">{c.contract_number}</td>
                  <td className="px-3 py-2">{c.contract_name}</td>
                  <td className="px-3 py-2 text-[11px]">{(c.countries || []).join(" · ") || "Global"}</td>
                  <td className="px-3 py-2 text-[11px] text-slate-500">
                    {c.start_date} → {c.end_date}
                  </td>
                  <td className="px-3 py-2 text-[12px]">{c.currency}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wider ${STATUS_TONE[c.state] || "bg-slate-100"}`}>
                      {c.state}
                    </span>
                  </td>
                </tr>
              ))}
              {contracts.length === 0 && !busy && (
                <tr><td colSpan={6} className="text-center py-8 text-[12px] text-slate-400">
                  No contracts match.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Section>

      {selectedId && (
        <ContractDetailDialog contractId={selectedId}
          isSuntronix={isSuntronix}
          onClose={() => setSelectedId(null)}
          onChanged={load} />
      )}
      {showNew && (
        <NewContractDialog onClose={() => setShowNew(false)}
                             onCreated={() => { setShowNew(false); load(); }} />
      )}
      {showEligibility && (
        <EligibilityDialog onClose={() => setShowEligibility(false)} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Contract detail (entitlements + status controls)
// ---------------------------------------------------------------------------
function ContractDetailDialog({ contractId, isSuntronix, onClose, onChanged }) {
  const [contract, setContract] = useState(null);
  const [busy, setBusy] = useState(false);
  const [addCountry, setAddCountry] = useState("");
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/contract-centre/contracts/${contractId}`);
      setContract(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Load failed");
    }
  }, [contractId]);
  useEffect(() => { load(); }, [load]);

  const transitionTo = async (to) => {
    if (!window.confirm(`Move to ${to}?`)) return;
    setBusy(true);
    try {
      await api.post(`/contract-centre/contracts/${contractId}/status`,
                                     { to_status: to });
      toast.success(`Contract → ${to}`);
      onChanged?.();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Transition refused");
    } finally { setBusy(false); }
  };

  const previewCountryAmendment = async () => {
    const iso = addCountry.trim().toUpperCase();
    if (!iso || iso.length < 2) { toast.error("Enter an ISO country code (e.g. IN, SG)"); return; }
    if ((contract?.countries || []).includes(iso)) { toast.error(`${iso} is already covered`); return; }
    setBusy(true);
    try {
      const nextCountries = [...(contract?.countries || []), iso];
      const body = {
        kind: "new_country",
        title: `Add ${iso} to covered countries`,
        effective_from: new Date().toISOString().slice(0, 10),
        summary: `Extend geographic scope to include ${iso}.`,
        entitlement_updates: [],
        contract_patch: { countries: nextCountries },
      };
      const { data } = await api.post(
        `/contract-centre/contracts/${contractId}/amendments/preview`, body);
      setPreview({ body, impact: data, iso });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Preview failed");
    } finally { setBusy(false); }
  };

  const confirmApplyAmendment = async () => {
    if (!preview) return;
    setBusy(true);
    try {
      await api.post(`/contract-centre/contracts/${contractId}/amendments`, preview.body);
      toast.success(`Amendment applied — ${preview.iso} added`);
      setAddCountry(""); setPreview(null);
      onChanged?.();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Amendment failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="cc-detail-dialog">
        <DialogHeader>
          <DialogTitle>{contract?.contract_number || "…"}</DialogTitle>
          <div className="text-[12px] text-slate-500">
            {contract?.contract_name}
          </div>
        </DialogHeader>
        {contract && (
          <div className="space-y-4 text-[13px]">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <F label="Status"><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold tracking-wider ${STATUS_TONE[contract.state] || "bg-slate-100"}`}>{contract.state}</span></F>
              <F label="Type">{contract.contract_type}</F>
              <F label="Currency">{contract.currency}</F>
              <F label="Term">{contract.start_date} → {contract.end_date}</F>
              <F label="Renewal date">{contract.renewal_date || "—"}</F>
              <F label="Billing frequency">{contract.billing_frequency}</F>
              <F label="Payment terms">{contract.payment_terms}</F>
              <F label="PO requirement">{contract.po_requirement}</F>
              <F label="Countries">{(contract.countries || []).join(" · ") || "Global"}</F>
              <F label="Suntronix owner">{contract.suntronix_owner_email || "—"}</F>
              <F label="Customer owner">{contract.customer_owner_email || "—"}</F>
              <F label="Monthly minimum">{contract.monthly_minimum_amount ? `${contract.currency} ${contract.monthly_minimum_amount.toLocaleString()}` : "—"}</F>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Entitlements</div>
              <div className="border rounded overflow-hidden">
                <table className="w-full text-[12px]">
                  <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                    <tr>
                      <th className="text-left px-2 py-1.5">Service</th>
                      <th className="text-left px-2 py-1.5">Inclusion</th>
                      <th className="text-right px-2 py-1.5">Rate</th>
                      <th className="text-left px-2 py-1.5">SLA</th>
                      <th className="text-left px-2 py-1.5">Billing trigger</th>
                      <th className="text-left px-2 py-1.5">PO</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(contract.entitlements || []).map((e) => (
                      <tr key={e.id} className="border-t border-slate-100">
                        <td className="px-2 py-1.5 font-mono text-[11px]">{e.service_code}</td>
                        <td className="px-2 py-1.5"><span className="inline-flex items-center px-1.5 py-0.5 rounded bg-slate-100 text-[10px] uppercase tracking-wider">{e.inclusion_type}</span></td>
                        <td className="px-2 py-1.5 text-right tabular-nums">
                          {e.rate != null ? `${e.currency || contract.currency} ${e.rate.toLocaleString()}`
                            : e.volume_tiers?.length ? `${e.volume_tiers.length} tiers` : "—"}
                        </td>
                        <td className="px-2 py-1.5">{e.sla_target || "—"}</td>
                        <td className="px-2 py-1.5 text-[11px]">{e.billing_trigger || "—"}</td>
                        <td className="px-2 py-1.5 text-[11px]">{e.po_requirement || "(contract default)"}</td>
                      </tr>
                    ))}
                    {(!contract.entitlements || contract.entitlements.length === 0) && (
                      <tr><td colSpan={6} className="px-2 py-4 text-center text-slate-400">No entitlements configured.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {isSuntronix && (
              <div className="flex flex-wrap gap-2 pt-2 items-center">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 self-center mr-2">Status →</div>
                {["ACTIVE","SUSPENDED","TERMINATED","SUPERSEDED"].map((s) => (
                  <Button key={s} size="sm" variant="outline"
                    onClick={() => transitionTo(s)} disabled={busy}
                    data-testid={`cc-transition-${s}`}>
                    {s}
                  </Button>
                ))}
                <div className="flex items-center gap-1 ml-4">
                  <Input value={addCountry} onChange={(e) => setAddCountry(e.target.value.toUpperCase())}
                             placeholder="ISO country (IN, SG…)"
                             className="h-8 text-[12px] w-40"
                             data-testid="cc-amend-country-input" />
                  <Button size="sm" variant="outline" onClick={previewCountryAmendment}
                              disabled={busy || !addCountry.trim()}
                              data-testid="cc-amend-country-btn">
                    Preview impact
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
      {preview && (
        <Dialog open onOpenChange={(v) => { if (!v) setPreview(null); }}>
          <DialogContent className="max-w-lg" data-testid="cc-amend-preview">
            <DialogHeader>
              <DialogTitle>Amendment impact · Add {preview.iso}</DialogTitle>
              <div className="text-[12px] text-slate-500">
                Effective {preview.body.effective_from}.  Nothing is saved until you confirm.
              </div>
            </DialogHeader>
            <div className="space-y-3 text-[13px]">
              <div className="grid grid-cols-2 gap-3">
                <F label="Newly added countries">{(preview.impact.newly_added_countries || []).join(", ") || "—"}</F>
                <F label="Effective from">{preview.impact.effective_from}</F>
                <F label="Open requests on tenant">{preview.impact.affected_open_requests}</F>
                <F label="Open quotations on tenant">{preview.impact.affected_open_quotations}</F>
              </div>
              <div className={`rounded border p-3 ${preview.impact.would_unblock_total > 0 ? "bg-emerald-50 border-emerald-200" : "bg-slate-50 border-slate-200"}`}
                        data-testid="cc-amend-preview-unblock">
                <div className="text-[11px] uppercase tracking-wider mb-1">
                  Would immediately unblock
                </div>
                <div className="text-2xl font-heading font-semibold tabular-nums">
                  {preview.impact.would_unblock_total}
                </div>
                <div className="text-[11px] text-slate-600 mt-1">
                  {Object.entries(preview.impact.would_unblock || {}).map(([k, list]) => (
                    <span key={k} className="mr-3">{k}: <strong>{list.length}</strong></span>
                  ))}
                </div>
                {preview.impact.would_unblock_total > 0 && (
                  <div className="mt-2 text-[11px] text-slate-700 max-h-32 overflow-y-auto space-y-0.5">
                    {Object.entries(preview.impact.would_unblock).flatMap(([kind, list]) =>
                      list.slice(0, 8).map((r) => (
                        <div key={`${kind}-${r.id}`} className="font-mono truncate">
                          {kind.slice(0, -1)} · {r.id?.slice(0, 8)}… {r.status || r.action || ""}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPreview(null)}>Cancel</Button>
              <Button onClick={confirmApplyAmendment} disabled={busy}
                        data-testid="cc-amend-preview-confirm">
                {busy ? "Applying…" : "Confirm & apply amendment"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
}
function EligibilityDialog({ onClose }) {
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [serviceCode, setServiceCode] = useState("");
  const [country, setCountry] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [c, s] = await Promise.all([
          api.get("/master-companies"),
          api.get("/contract-centre/service-catalogue"),
        ]);
        const cRows = Array.isArray(c.data) ? c.data : (c.data?.items || []);
        setCustomers(cRows);
        setServices(s.data?.items || []);
      } catch { /* leave empty */ }
    })();
  }, []);

  const run = async () => {
    if (!customerId || !serviceCode) { toast.error("Pick a customer + service"); return; }
    setBusy(true); setResult(null);
    try {
      const { data } = await api.get("/contract-centre/eligibility", {
        params: {
          master_company_id: customerId,
          service_code: serviceCode,
          country: country || undefined,
        },
      });
      setResult(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Resolver failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="cc-eligibility-dialog">
        <DialogHeader>
          <DialogTitle>Test eligibility</DialogTitle>
          <div className="text-[12px] text-slate-500">
            Preview what Preflight, Orders and Deployments will see for a (customer × service × country) combination.
          </div>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-3">
          <label className="block col-span-2">
            <div className="text-[11px] text-slate-500 mb-1">Customer</div>
            <select value={customerId} onChange={(e) => setCustomerId(e.target.value)}
              className="w-full border rounded h-9 px-2 text-[13px]" data-testid="cc-el-customer">
              <option value="">— pick a customer —</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name || c.code}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="text-[11px] text-slate-500 mb-1">Country</div>
            <Input value={country} onChange={(e) => setCountry(e.target.value.toUpperCase())}
                     placeholder="IN"
                     className="h-9 text-[13px]" />
          </label>
          <label className="block col-span-3">
            <div className="text-[11px] text-slate-500 mb-1">Service</div>
            <select value={serviceCode} onChange={(e) => setServiceCode(e.target.value)}
              className="w-full border rounded h-9 px-2 text-[13px]" data-testid="cc-el-service">
              <option value="">— pick a service —</option>
              {services.map((s) => <option key={s.code} value={s.code}>{s.name || s.code}</option>)}
            </select>
          </label>
        </div>
        {result && (
          <div className={`mt-3 p-3 rounded border ${result.eligible ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}
                  data-testid="cc-el-result">
            <div className="flex items-center gap-2 mb-2">
              {result.eligible
                ? <><ShieldCheck className="h-4 w-4 text-emerald-700" /><strong className="text-emerald-900">Eligible</strong></>
                : <><Ban className="h-4 w-4 text-red-700" /><strong className="text-red-900">Not eligible</strong></>}
            </div>
            {!result.eligible && (
              <ul className="text-[12px] text-red-800 list-disc pl-5">
                {(result.blocking_reasons || []).map((r) => <li key={r}>{r}</li>)}
              </ul>
            )}
            <div className="grid grid-cols-2 gap-2 mt-2 text-[12px]">
              {result.contract && <F label="Contract">{result.contract.contract_number} · {result.contract.state}</F>}
              {result.entitlement && <F label="Entitlement">{result.entitlement.inclusion_type}</F>}
              {result.rate && <F label="Rate">{result.rate.currency || result.currency} {result.rate.rate?.toLocaleString?.() || result.rate.rate}</F>}
              {result.sla && <F label="SLA">{result.sla.target}</F>}
              <F label="PO required">{result.po_required ? `${result.po_kind}` : "no"}</F>
              <F label="Approval required">{result.approval_required ? "yes" : "no"}</F>
              {result.billing_trigger && <F label="Billing trigger">{result.billing_trigger}</F>}
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          <Button onClick={run} disabled={busy} data-testid="cc-el-run">
            {busy ? "Resolving…" : "Resolve"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Minimal New-contract dialog (Suntronix only)
// ---------------------------------------------------------------------------
function NewContractDialog({ onClose, onCreated }) {
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({
    contract_number: "", contract_name: "",
    master_company_id: "", legal_entities: "",
    countries: "", contract_type: "MSA",
    start_date: new Date().toISOString().slice(0, 10),
    end_date: new Date(Date.now() + 365 * 86400e3).toISOString().slice(0, 10),
    currency: "INR", billing_frequency: "monthly",
    payment_terms: "Net 30", po_requirement: "per_transaction",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/master-companies");
        setCustomers(Array.isArray(data) ? data : (data?.items || []));
      } catch { /* leave empty */ }
    })();
  }, []);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    if (!form.contract_number || !form.contract_name || !form.master_company_id) {
      toast.error("Number, name and customer are required.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/contract-centre/contracts", {
        ...form,
        legal_entities: form.legal_entities
          ? form.legal_entities.split(",").map((s) => s.trim()).filter(Boolean)
          : [form.master_company_id],
        countries: form.countries
          ? form.countries.split(",").map((s) => s.trim().toUpperCase()).filter(Boolean)
          : [],
      });
      toast.success("Draft contract created");
      onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="cc-new-dialog">
        <DialogHeader>
          <DialogTitle>New contract</DialogTitle>
          <div className="text-[12px] text-slate-500">Creates a DRAFT.  Add entitlements after activation.</div>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <div className="text-[11px] text-slate-500 mb-1">Contract number</div>
            <Input value={form.contract_number} onChange={(e) => set("contract_number", e.target.value)}
                     placeholder="MSA-2026-XXX-01" className="h-9 text-[13px]"
                     data-testid="cc-new-number" />
          </label>
          <label className="block">
            <div className="text-[11px] text-slate-500 mb-1">Contract name</div>
            <Input value={form.contract_name} onChange={(e) => set("contract_name", e.target.value)}
                     className="h-9 text-[13px]" data-testid="cc-new-name" />
          </label>
          <label className="block col-span-2">
            <div className="text-[11px] text-slate-500 mb-1">Customer</div>
            <select value={form.master_company_id}
              onChange={(e) => set("master_company_id", e.target.value)}
              className="w-full border rounded h-9 px-2 text-[13px]" data-testid="cc-new-customer">
              <option value="">— pick a customer —</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name || c.code}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="text-[11px] text-slate-500 mb-1">Start date</div>
            <Input type="date" value={form.start_date} onChange={(e) => set("start_date", e.target.value)} className="h-9" />
          </label>
          <label className="block">
            <div className="text-[11px] text-slate-500 mb-1">End date</div>
            <Input type="date" value={form.end_date} onChange={(e) => set("end_date", e.target.value)} className="h-9" />
          </label>
          <label className="block">
            <div className="text-[11px] text-slate-500 mb-1">Currency</div>
            <select value={form.currency} onChange={(e) => set("currency", e.target.value)}
              className="w-full border rounded h-9 px-2 text-[13px]">
              {["INR","USD","EUR","GBP","SGD","AUD","AED"].map((c) => <option key={c}>{c}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="text-[11px] text-slate-500 mb-1">Countries (comma-separated ISO)</div>
            <Input value={form.countries} onChange={(e) => set("countries", e.target.value)}
                     placeholder="IN,SG" className="h-9 text-[13px]" />
          </label>
          <label className="block">
            <div className="text-[11px] text-slate-500 mb-1">PO requirement</div>
            <select value={form.po_requirement} onChange={(e) => set("po_requirement", e.target.value)}
              className="w-full border rounded h-9 px-2 text-[13px]">
              {["not_required","per_transaction","per_month","blanket","annual",
                 "country_specific","legal_entity_specific"].map((k) => <option key={k}>{k}</option>)}
            </select>
          </label>
          <label className="block">
            <div className="text-[11px] text-slate-500 mb-1">Payment terms</div>
            <Input value={form.payment_terms} onChange={(e) => set("payment_terms", e.target.value)} className="h-9 text-[13px]" />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="cc-new-submit">
            {busy ? "Creating…" : "Create draft"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-[13px]">{children ?? "—"}</div>
    </div>
  );
}
